import { ReactNode, forwardRef } from 'react';
import { Badge } from './Badge';
import type { BadgeProps, BadgeColor, BadgeVariant } from './Badge';
import { getStatusConfig, type EntityType } from './StatusChip/config';

export interface StatusChipProps extends Omit<BadgeProps, 'color' | 'variant'> {
  entityType?: EntityType;
  status: string;
  // Allow explicit override of mapped values
  color?: BadgeColor;
  variant?: BadgeVariant;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  // Allow custom label override
  label?: string;
}

/**
 * StatusChip - Universal status badge component
 *
 * Displays status badges with automatic mapping based on entity type and status.
 * Supports three levels of configuration priority:
 * 1. Explicit props (highest priority - overrides everything)
 * 2. Entity type specific override (if defined)
 * 3. Default status mapping (base level)
 *
 * @example
 * ```tsx
 * // Basic usage with default mapping
 * <StatusChip status="pending" />
 *
 * // With entity type (uses entity-specific override if available)
 * <StatusChip entityType="import_mapping" status="created" />
 *
 * // Override specific props
 * <StatusChip entityType="file" status="uploading" color="primary" />
 * ```
 */
export const StatusChip = forwardRef<HTMLDivElement, StatusChipProps>(
  (
    {
      entityType,
      status,
      color: explicitColor,
      variant: explicitVariant,
      startIcon: explicitStartIcon,
      endIcon: explicitEndIcon,
      label: explicitLabel,
      children,
      ...badgeProps
    },
    ref
  ) => {
    // Get mapped configuration
    const mappedConfig = getStatusConfig(status, entityType);

    // Priority: explicit props > mapped config
    const finalColor = explicitColor ?? mappedConfig.color ?? 'default';
    const finalVariant = explicitVariant ?? mappedConfig.variant ?? 'surface';
    const finalStartIcon = explicitStartIcon ?? (mappedConfig.icon ? mappedConfig.icon() : undefined);
    const finalEndIcon = explicitEndIcon;
    const finalLabel = explicitLabel ?? mappedConfig.label ?? status;
    const finalChildren = children ?? finalLabel;

    return (
      <Badge
        ref={ref}
        color={finalColor}
        variant={finalVariant}
        startIcon={finalStartIcon}
        endIcon={finalEndIcon}
        {...badgeProps}
      >
        {finalChildren}
      </Badge>
    );
  }
);

StatusChip.displayName = 'StatusChip';

